/* =========================================================================
 * THE STATE GOVERNING BODIES — every entity that crowns a high-school title.
 *
 * Brian: "every state entity should have a place on our board… include the
 * state governing body official logo" · "We have permission from every one. You
 * can pull them. They should live in hubspot." · "make sure we also get the
 * independent championships from every state and private schools."
 *
 * ⚠️ EVERY URL BELOW WAS FETCHED AND CONFIRMED TO LOAD (2026-09-01). Every
 * `logoSource` was loaded and confirmed to return an image. This is not a list
 * of plausible domains — the first pass WAS that, and eight of them were wrong:
 *
 *   DC  dcsaasports.org  — ⚠️ LAPSED, now serves a gambling site. Never link it.
 *   AR  arkansasactivities.org — dead. AAA runs on ahsaa.org (confusingly close
 *       to ALABAMA's ahsaa.com — two different states, one letter apart).
 *   DE  doe.k12.de.us/diaa — dead, no redirect.
 *   CT  casciac.org — now a content-free splash; CIAC lives on ciac.fpsports.org.
 *   HI  sportshigh.com — 301s to hhsaa.org.
 *   OK  ossaa.com — 301s to ossaaillustrated.com.
 *   ME  mpa.cc — bare domain errors; needs www.
 *   NY  chsaa.org is COLORADO's CHSAA. New York's is chsaany.org — linking the
 *       first would have put a Colorado body on a New York record.
 *
 * ⚠️ THE INDEPENDENT / PRIVATE BODIES ARE THE POINT, NOT A FOOTNOTE.
 * SR sells to those schools. The first pass listed 10 states with a second
 * association; the real number is far higher, and several states have THREE or
 * FOUR. Alabama alone has AISA, ACSC and ACAA. Texas has TAPPS, TCAF, TAIAO,
 * TCSAAL and T-CAL. Every one crowns titles somebody buys rings for.
 *
 * ⚠️ FOUR HONEST FLAGS, because "we didn't find one" and "there isn't one" are
 * different answers and conflating them is how a category stays invisible:
 *   searched   — looked properly, found nothing. NOT proof of absence.
 *   multiState — its titles span several states; a member does NOT win a
 *                "state championship". NEPSAC, MAIS, HCAA, T-CAL nationals.
 *   dormant    — site or body has gone quiet; newest content is years old.
 *   noSite     — the body demonstrably EXISTS and crowns titles but publishes
 *                no site (Kansas KCAA, Maine MAISAD, Montana MCAA). Recorded so
 *                it is visible, with nothing invented to fill the gap.
 *
 * ⚠️ `logo` VS `logoSource`. `logoSource` is the association's own URL and is
 * used ONCE, server-side, at ingest. `logo` is the HubSpot-hosted copy and is
 * the only thing that ever renders — `markFor()` refuses an http(s) value
 * outright, so the owned-assets rule cannot be broken by editing this file.
 * Hosted marks are stored on the backend, not here.
 *
 * `accent` is SR's own palette for wayfinding, NOT the association's brand
 * colour. Claiming otherwise would be inventing brand data.
 * ========================================================================= */

const B = (acronym, name, site, logoSource, champs, kind, flags = {}) =>
  ({ acronym, name, site, logoSource: logoSource || null, champs: champs || null, kind, ...flags })

export const STATE_BODIES = [
  { st: 'AL', state: 'Alabama', acronym: 'AHSAA', name: 'Alabama High School Athletic Association',
    site: 'https://www.ahsaa.com/', accent: '#8B2E2E',
    logoSource: 'https://www.ahsaa.com/Portals/0/try%203.jpg',
    champs: 'https://www.ahsaa.com/Calendar-and-Events/Championship-Info/Championship-Dates',
    note: '⚠️ AHSAA approved a 2026-28 classification that SEPARATES public and private schools for postseason play — private members crown their own AHSAA championships from fall 2026.',
    others: [
      B('AISA', 'Alabama Independent School Association', 'https://aisaonline.org/', 'https://aisaonline.org/wp-content/uploads/2019/09/9Asset-1.svg', 'https://aisaonline.org/athletics/', 'independent'),
      B('ACSC', 'Alabama Christian Sports Conference', 'http://www.acscsports.org/', null, 'http://www.acscsports.org/calendar.html', 'christian', { insecure: true }),
      B('ACAA', 'Alabama Christian Athletic Association (ACEA)', 'https://www.alabamachristianed.com/acaa-athletics.html', 'https://www.alabamachristianed.com/uploads/1/5/0/1/150160896/img-0051.jpg', 'https://www.alabamachristianed.com/yearly-calendar.html', 'christian'),
    ] },

  { st: 'AK', state: 'Alaska', acronym: 'ASAA', name: 'Alaska School Activities Association',
    site: 'https://asaa.org/', accent: '#2E5E8B',
    logoSource: 'https://asaa.org/wp-content/uploads/asaa-logo.png',
    champs: 'https://asaa.org/resources/championship-programs/',
    note: 'Private and parochial schools are ASAA members and play for ASAA titles.', searched: true },

  { st: 'AZ', state: 'Arizona', acronym: 'AIA', name: 'Arizona Interscholastic Association',
    site: 'https://aiaonline.org/', accent: '#B4562A',
    logoSource: 'https://aiaonline.org/assets/images/aia.png',
    champs: 'https://aiaonline.org/sports',
    others: [
      B('CAA', 'Canyon Athletic Association', 'https://azcaa.com/', 'https://azcaa.nyc3.digitaloceanspaces.com/images/caa_logo.png', 'https://azcaa.com/sports', 'independent'),
    ] },

  { st: 'AR', state: 'Arkansas', acronym: 'AAA', name: 'Arkansas Activities Association',
    site: 'https://www.ahsaa.org/', accent: '#7A2E3E',
    logoSource: 'https://cmsv2-assets.apptegy.net/uploads/34192/logo/37255/3-27ab8605-0414-49ff-8f35-bb4ae8fce67c-.png',
    champs: 'https://www.ahsaa.org/events',
    note: '⚠️ Corrected — arkansasactivities.org is dead. Note ahsaa.ORG is Arkansas; ahsaa.COM is Alabama.',
    others: [
      B('HCAA', 'Heartland Christian Athletic Association', 'http://www.heartlandathletics.com/clubs/5161', null, 'http://www.heartlandathletics.com/clubs/5161/events', 'christian', { multiState: true, insecure: true }),
      /* Surfaced by the harvest — 7 Arkansas members, and it was an orphan
       * roster until it was added here. */
      B('NCSAA', 'National Christian School Athletic Association', 'https://www.ncsaa.org', null, null, 'christian', { multiState: true }),
    ] },

  { st: 'CA', state: 'California', acronym: 'CIF', name: 'California Interscholastic Federation',
    site: 'https://cifstate.org', accent: '#2A6B7A',
    logoSource: 'https://cifstate.org/assets/images/Primary_Logo.png',
    champs: 'https://cifstate.org/landing/index',
    note: '⚠️ Federated into 10 SECTIONS that crown their own section championships feeding the state final. CIF covers private and parochial schools directly — whole private leagues are section members.', searched: true,
    /* ⚠️ THE TEN SECTIONS ARE ENTITIES, NOT A FOOTNOTE. CIF has no statewide
     * member list because it does not have a statewide membership — each
     * section holds its own schools and crowns its own section championships,
     * which is why a first harvest of "CIF" came back with zero.
     *
     * Listing them only in the note above made 1,669 harvested California
     * schools UNREACHABLE from the board: verify-bodies.mjs §4 flagged them as
     * orphans, harvested and validated and visible nowhere. Shipped and
     * unreachable is the same as absent — the same failure that once hid the
     * college rosters three clicks deep. They are real entities now. */
    others: [
      B('CIF-SS', 'CIF Southern Section', 'https://www.cifss.org', null, 'https://www.cifss.org/championships/', 'section'),
      B('CIF-SJS', 'CIF Sac-Joaquin Section', 'https://www.cifsjs.org', 'https://5starassets.blob.core.windows.net/state-association-sites/9/6/images/6-6b43452b-a6a5-48c7-9843-2fa564b670a4-.png', null, 'section'),
      B('CIF-NCS', 'CIF North Coast Section', 'https://www.cifncs.org', null, null, 'section'),
      B('CIF-LACS', 'CIF Los Angeles City Section', 'https://www.cif-la.org', null, null, 'section'),
      B('CIF-CCS', 'CIF Central Coast Section', 'https://www.cifccs.org', null, null, 'section'),
      B('CIF-CS', 'CIF Central Section', 'https://www.cifcs.org', null, null, 'section'),
      B('CIF-SDS', 'CIF San Diego Section', 'https://www.cifsds.org', null, null, 'section'),
      B('CIF-NS', 'CIF Northern Section', 'https://www.cifns.org', null, null, 'section'),
      B('CIF-SF', 'CIF San Francisco Section', 'https://www.cifsf.org', 'https://wordpress-conference-production-public.s3.amazonaws.com/wp-content/uploads/sites/9/2025/07/CIFSF-LOGO-Transparent-1.png', null, 'section'),
      B('CIF-Oakland', 'CIF Oakland Section', 'https://www.cifoaklandsection.org', null, null, 'section'),
    ] },

  { st: 'CO', state: 'Colorado', acronym: 'CHSAA', name: 'Colorado High School Activities Association',
    site: 'https://chsaanow.com', accent: '#8B5E2A',
    logoSource: 'https://chsaanow.com/brand/logos/primary/logo.svg',
    champs: 'https://chsaa.co/events',
    note: 'Covers private/parochial directly — Valor Christian, Holy Family and Lutheran are CHSAA state champions.', searched: true },

  { st: 'CT', state: 'Connecticut', acronym: 'CIAC', name: 'Connecticut Interscholastic Athletic Conference',
    site: 'https://ciac.fpsports.org/', accent: '#3E5E8B',
    logoSource: 'https://ciac.fpsports.org/resources/_LogoSmall.png',
    champs: 'https://ciac.fpsports.org/DashboardTournamentCentral.aspx',
    note: '⚠️ Corrected — casciac.org is now a content-free splash. CIAC covers parochial and private members directly; CT boarding schools play NEPSAC (regional, not a state title).', searched: true },

  { st: 'DE', state: 'Delaware', acronym: 'DIAA', name: 'Delaware Interscholastic Athletic Association',
    site: 'https://education.delaware.gov/diaa/', accent: '#2A7A5E',
    logoSource: 'https://education.delaware.gov/diaa/wp-content/uploads/sites/2/2021/03/DIAA_logo_300.jpg',
    champs: 'https://education.delaware.gov/diaa/sport_championships/',
    note: '⚠️ Corrected — doe.k12.de.us/diaa is dead with no redirect. Privates (Salesianum, Sanford, Tower Hill) are full DIAA members.', searched: true },

  { st: 'DC', state: 'District of Columbia', acronym: 'DCSAA', name: 'District of Columbia State Athletic Association',
    site: 'https://www.dcsaasports.com/', accent: '#5E3E8B',
    logoSource: 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/801/2023/09/18004513/cropped-site-title-192x192.png',
    champs: 'https://www.dcsaasports.com/dcsaa-2025-2026-championships-schedule/',
    note: '⚠️ dcsaasports.ORG HAS LAPSED and now serves a gambling site — never link it. DCSAA already includes public, charter AND private schools.',
    others: [
      B('DCIAA', 'DC Interscholastic Athletic Association (public league)', 'https://www.thedciaa.com/', 'https://static.wixstatic.com/media/98fcc7_77b1c510b1dc42e2b3027791cbbabdd2~mv2.png', 'https://www.thedciaa.com/champions', 'league'),
      B('WCAC', 'Washington Catholic Athletic Conference', 'https://www.wcacsports.com/', 'https://www.wcacsports.com/images/setup/WCAC_Logo.png', 'https://www.wcacsports.com/navbar-championships-history', 'private', { multiState: true }),
      B('ISL', 'Independent School League (girls)', 'https://www.islathletics.com/', 'https://www.islathletics.com/images/setup/Primary_Logo_White.png', null, 'independent'),
      B('IAC', 'Interstate Athletic Conference (boys)', 'https://www.iacathletics.com/', 'https://www.iacathletics.com/assets/favicons/apple-touch-icon.png', 'https://www.iacathletics.com/20252026-championships', 'independent'),
    ] },

  { st: 'FL', state: 'Florida', acronym: 'FHSAA', name: 'Florida High School Athletic Association',
    site: 'https://fhsaa.com/', accent: '#B4762A',
    logoSource: 'https://dbukjj6eu5tsf.cloudfront.net/fhsaa.org/images/responsive/main_logo_lg2.svg',
    champs: 'https://fhsaa.com/calendar.aspx',
    others: [
      B('SSAA', 'Sunshine State Athletic Association', 'https://www.sunshinestateathletics.com/', 'https://assets.sportsgravy.com/SYSTEM/wrggtj1auo0ffflq3odat72t.png', 'https://www.sunshinestateathletics.com/all-sports.html', 'independent'),
      B('FICAA', 'Florida Independent Christian Athletic Association', 'https://ficaa.org/', 'https://images.squarespace-cdn.com/content/v1/5e9621101f099b2001040700/1587066274137-QPA1EGD3GVR3MUMUXTDE/Website+Menu+Logo.png', 'https://ficaa.org/calendar', 'christian'),
      B('FCC', 'Florida Christian Conference', 'http://fccsports.net/', null, 'http://fccsports.net/schedules.htm', 'christian', { insecure: true }),
    ] },

  { st: 'GA', state: 'Georgia', acronym: 'GHSA', name: 'Georgia High School Association',
    site: 'https://www.ghsa.net/', accent: '#8B2E4E',
    logoSource: 'https://www.ghsa.net/sites/default/files/ghsa_logo_header_2_1.png',
    champs: 'https://www.ghsa.net/state-championships',
    others: [
      B('GIAA', 'Georgia Independent Athletic Association', 'https://www.giaasports.org/', 'https://giaasports.org/wp-content/uploads/2021/11/giaa-georgia-independent-athletic-association-logo.png', 'https://www.giaasports.org/sports/', 'independent'),
      B('GAPPS', 'Georgia Association of Private & Parochial Schools', 'https://gappsports.com/', 'https://gappsports.com/images/logo.png', 'https://gappsports.com/champions', 'private'),
      B('GCAA', 'Georgia Christian Athletic Association (GACS)', 'https://gacs.org/athletics/', 'https://gacs.org/wp-content/uploads/2022/07/logo-1.png', 'https://gacs.org/athletics/', 'christian'),
    ] },

  { st: 'HI', state: 'Hawaii', acronym: 'HHSAA', name: 'Hawaii High School Athletic Association',
    site: 'https://hhsaa.org/', accent: '#2A7A6B',
    logoSource: 'https://hhsaa.org/assets/2024/hhsaa-logo-2b1031b9f07031f815bacf209bef00fd7a7fc899bb4da185e032f9b72bdae190.png',
    champs: 'https://hhsaa.org/tournaments',
    note: '⚠️ Corrected — sportshigh.com 301s to hhsaa.org. 48 state championships in 20 sports.', searched: true },

  { st: 'ID', state: 'Idaho', acronym: 'IHSAA', name: 'Idaho High School Activities Association',
    site: 'https://idhsaa.org/', accent: '#5E7A2A',
    logoSource: 'https://idhsaa.org/asset/theme/logo_menu_brand.png',
    champs: 'https://idhsaa.org/state-tournaments', searched: true },

  { st: 'IL', state: 'Illinois', acronym: 'IHSA', name: 'Illinois High School Association',
    site: 'https://www.ihsa.org/', accent: '#2E4E8B',
    logoSource: 'https://ihsa-assets-prod.nyc3.cdn.digitaloceanspaces.com/images/page-graphic/ihsa-logo-web.png',
    champs: 'https://www.ihsa.org/calendar',
    others: [
      B('IACS', 'Illinois Association of Christian Schools', 'https://www.illinoisacs.org/', 'https://www.illinoisacs.org/wp-content/uploads/2025/02/IACS-Logo-Color-Variations_Full-Color-300x161.png', 'https://www.illinoisacs.org/athletics-standings-2/', 'christian'),
      B('LSA', 'Lutheran Sports Association', 'https://luthsports.org/', null, 'https://luthsports.org/sports/', 'christian'),
    ] },

  { st: 'IN', state: 'Indiana', acronym: 'IHSAA', name: 'Indiana High School Athletic Association',
    site: 'https://www.ihsaa.org/', accent: '#8B4E2A',
    logoSource: 'https://www.ihsaa.org/sites/default/files/ihsaa_logo.png',
    champs: 'https://www.ihsaa.org/sports',
    others: [
      B('IACS', 'Indiana Association of Christian Schools', 'https://www.indianaacs.org/', 'https://www.indianaacs.org/uploads/1/2/0/4/120411403/published/logo-only-2020final.png', 'https://www.indianaacs.org/general-information.html', 'christian'),
    ] },

  { st: 'IA', state: 'Iowa', acronym: 'IHSAA', name: 'Iowa High School Athletic Association (boys)',
    site: 'https://www.iahsaa.org/', accent: '#7A5E2A',
    logoSource: 'https://www.iahsaa.org/wp-content/uploads/2024/05/IHSAA-Red-State-480x329.png',
    champs: 'https://www.iahsaa.org/calendar/',
    note: '⚠️ TWO public associations. IHSAA crowns BOYS; IGHSAU crowns GIRLS. Reading one loses every championship of the other gender.',
    others: [
      B('IGHSAU', 'Iowa Girls High School Athletic Union', 'https://ighsau.org/', 'https://ighsau.org/img/containers/assets/brand/ighsau-logo-text.png/ce5ae60f13dba66eeb3060077570d521/ighsau-logo-text.png', 'https://ighsau.org/sports', 'girls'),
    ] },

  { st: 'KS', state: 'Kansas', acronym: 'KSHSAA', name: 'Kansas State High School Activities Association',
    site: 'https://kshsaa.org/', accent: '#2A5E7A',
    logoSource: 'https://www.kshsaa.org/Images/KSHSAA-Swoosh-White.png',
    champs: 'https://www.kshsaachamps.org/StateChampions/',
    others: [
      B('KCAA', 'Kansas Christian Athletic Association', null, null, null, 'christian', { noSite: true,
        evidence: 'Crowns its own titles — evidenced on member-school athletics pages (Chanute Christian Academy; KCAA Div II football, 2A state basketball). kcaasports.org/.com are NXDOMAIN. NOT FOUND, not absent.' }),
    ] },

  { st: 'KY', state: 'Kentucky', acronym: 'KHSAA', name: 'Kentucky High School Athletic Association',
    site: 'https://khsaa.org/', accent: '#3E2A7A',
    logoSource: 'https://khsaa.org/wp-content/themes/khsaa-theme/images/logo.png',
    champs: 'https://khsaa.org/sports-sport-activities/',
    others: [
      B('KCAA', 'Kentucky Christian Athletic Association', 'http://www.kcaaonline.com/', null, 'http://www.kcaaonline.com/Sports.html', 'christian', { insecure: true }),
      B('KCSAA', 'Kentucky Christian School Athletic Association', 'https://kcsaa.net/', null, 'https://kcsaa.net/basketball', 'christian'),
    ] },

  { st: 'LA', state: 'Louisiana', acronym: 'LHSAA', name: 'Louisiana High School Athletic Association',
    site: 'https://www.lhsaa.org/', accent: '#6B2A7A',
    logoSource: 'https://www.lhsaa.org/html/images/imgs/main-logo.svg',
    champs: 'https://www.lhsaa.org/champ-central',
    others: [
      B('MAIS', 'Mid-South Association of Independent Schools', 'https://home.msais.org/test2/index.php', 'https://home.msais.org/test2/pics/maislogo.png', 'https://home.msais.org/test2/code/athletics/viewbrackets.php', 'independent', { multiState: true }),
    ] },

  { st: 'ME', state: 'Maine', acronym: 'MPA', name: "Maine Principals' Association",
    site: 'https://www.mpa.cc/', accent: '#2A6B4E',
    logoSource: 'https://www.mpa.cc/resources/_LogoSmall.png',
    champs: 'https://www.mpa.cc/Reports/PastChampionsBySport.aspx',
    note: '⚠️ Bare mpa.cc errors — the www host is required.',
    others: [
      B('MAISAD', 'Maine Association of Independent Athletic Directors', null, null, null, 'independent', { noSite: true,
        evidence: "Existence confirmed on NEPSAC's own league registry. Crowns MAISAD titles for Maine independent/prep schools, separate from MPA. maisad.org is a parked lander." }),
    ] },

  { st: 'MD', state: 'Maryland', acronym: 'MPSSAA', name: 'Maryland Public Secondary Schools Athletic Association',
    site: 'https://www.mpssaa.org/', accent: '#8B6B2A',
    logoSource: 'https://www.mpssaa.org/wp-content/uploads/2025/10/mpssaa-logo.png',
    champs: 'https://www.mpssaa.org/sports/',
    others: [
      B('MIAA', 'Maryland Interscholastic Athletic Association (private, boys)', 'https://miaasports.net/', 'https://miaasports.net/images/setup/PRIMARY_LOGO.png', 'https://miaasports.net/miaa_championships', 'private'),
      B('IAAM', 'Interscholastic Athletic Association of Maryland (private, girls)', 'https://iaamsports.com/', 'https://iaamsports.com/wp-content/uploads/2025/04/IAAM-Red-Banner.png', 'https://iaamsports.com/soccer/championships', 'private'),
      B('MACSAC', 'Maryland Assn of Christian Schools Athletic Conference', 'https://www.macsmd.org/macsac', 'https://images.squarespace-cdn.com/content/v1/57e16d93d482e96cd834f670/0371ff4b-8bf9-4719-ae45-6a4cc280c4dc/MACSLogo2025.png?format=1500w', 'https://www.macsmd.org/s/MACSAC-Championship-Listing.pdf', 'christian'),
      B('MCST', 'Maryland Christian School Tournament', 'https://mcstonline.sportngin.com/', null, 'https://mcstonline.sportngin.com/tourneys', 'christian'),
    ] },

  { st: 'MA', state: 'Massachusetts', acronym: 'MIAA', name: 'Massachusetts Interscholastic Athletic Association',
    site: 'https://www.miaa.net/', accent: '#2E3E7A',
    logoSource: 'https://www.miaa.net/sites/default/files/2024-06/miaa-white-logo.svg',
    champs: 'https://www.miaa.net/sports',
    note: '⚠️ Not the Maryland MIAA — same acronym, different body. The MA independent lane is NEPSAC, whose titles are NEW ENGLAND titles, not state titles.',
    others: [
      B('NEPSAC', 'New England Preparatory School Athletic Council', 'https://nepsac.org/', 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/328/2022/05/05100851/np.png', 'https://nepsac.org/tournaments/', 'independent', { multiState: true }),
      B('ISL', 'Independent School League', 'https://www.islsports.org/', 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/41/2021/06/07192120/Logo-for-Website.jpg', 'https://www.islsports.org/tournaments/', 'league'),
    ] },

  { st: 'MI', state: 'Michigan', acronym: 'MHSAA', name: 'Michigan High School Athletic Association',
    site: 'https://www.mhsaa.com/', accent: '#2A4E6B',
    logoSource: 'https://www.mhsaa.com/themes/custom/mhsaa/images/logo.png',
    champs: 'https://www.mhsaa.com/sports',
    others: [
      B('MCSAA', 'Michigan Christian Schools Athletic Association', 'https://mcsaa.com/', 'https://framerusercontent.com/images/zw3LuA6CQ0JXkihLgIjVqsFCJc.jpg', 'https://mcsaa.com/tournamentinfo', 'christian'),
      B('MACS', 'Michigan Association of Christian Schools', 'https://www.macshome.org/', 'https://www.macshome.org/wp-content/uploads/2020/04/Macs-Web-Logo-1.png', 'https://www.macshome.org/locations/', 'christian'),
      B('MHSSA', 'Michigan Home School Sports Association', 'https://www.mhssa.org/', 'https://images.squarespace-cdn.com/content/v1/5a53bd3390badec229af6289/1515790760623-SXKIXDBQ7J9P19Z5G1EE/Michigan+Home+School+Sports+Association-logo.png', null, 'homeschool'),
    ] },

  { st: 'MN', state: 'Minnesota', acronym: 'MSHSL', name: 'Minnesota State High School League',
    site: 'https://www.mshsl.org/', accent: '#7A2A3E',
    logoSource: 'https://www.mshsl.org/themes/custom/mshsl/images/logo.svg',
    champs: 'https://www.mshsl.org/tournaments',
    others: [
      B('MCAC', 'Minnesota Christian Athletic Conference', 'https://mcacathletics.org/', 'https://mcacathletics.org/wp-content/uploads/2024/09/MCAC_IconLogo_Dark.png', 'https://mcacathletics.org/state-tournaments/', 'christian'),
    ] },

  { st: 'MS', state: 'Mississippi', acronym: 'MHSAA', name: 'Mississippi High School Activities Association',
    site: 'https://www.misshsaa.com/', accent: '#8B3E2A',
    logoSource: 'https://www.misshsaa.com/wp-content/uploads/2016/03/MHSAAWebLogo2.png',
    champs: 'https://www.misshsaa.com/category/championships/',
    others: [
      B('MAIS', 'Mid-South Association of Independent Schools', 'https://home.msais.org/test2/index.php', 'https://home.msais.org/test2/pics/maislogo.jpg', 'https://home.msais.org/test2/code/athletics/viewbrackets.php', 'independent', { multiState: true }),
      B('MSAHA', 'Mid-South Association of Homeschool Athletics', 'https://msaha.org/', 'https://images.squarespace-cdn.com/content/v1/68f599db7c51de304886f935/4139e273-e799-4f4c-a952-406527bf9cd5/cropped-MSAHA-Logo-Star-100x100-1.jpg?format=1500w', 'https://msaha.org/history/past-champions/', 'homeschool', { multiState: true }),
    ] },

  { st: 'MO', state: 'Missouri', acronym: 'MSHSAA', name: 'Missouri State High School Activities Association',
    site: 'https://www.mshsaa.org/', accent: '#5E2A6B',
    logoSource: 'https://www.mshsaa.org/apple-touch-icon.png',
    champs: 'https://www.mshsaa.org/Activities/',
    others: [
      B('MOCSAA', 'Missouri Christian School Athletic Association', 'https://mocsaa.com/', 'https://mocsaa.com/wp-content/uploads/2026/04/MoCSAA-Logo-Collegiate-COB_Gray-scaled-2.png', 'https://mocsaa.com/champions/', 'christian',
        { note: '⚠️ The old mcsaa.us domain is NXDOMAIN and is still what search returns — do not link it.' }),
    ] },

  { st: 'MT', state: 'Montana', acronym: 'MHSA', name: 'Montana High School Association',
    site: 'https://www.mhsa.org/', accent: '#4E6B2A',
    logoSource: 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/152/2025/03/17055525/Logo-Header.png',
    champs: 'https://www.mhsa.org/sports-activities/',
    others: [
      B('MCAA', 'Montana Christian Athletic Association', null, null, null, 'christian', { noSite: true,
        evidence: 'Coordinates athletics for 13 non-MHSA Montana high schools. A 2026 MCAA Basketball State Tournament (Great Falls) is documented; its only web surface is a dormant groups.io page. Body ACTIVE, no citable site.' }),
    ] },

  { st: 'NE', state: 'Nebraska', acronym: 'NSAA', name: 'Nebraska School Activities Association',
    site: 'https://nsaahome.org/', accent: '#7A4E2A',
    logoSource: 'https://nsaahome.org/wp-content/uploads/2020/12/nsaalogotransparent250.png',
    champs: 'https://nsaahome.org/nsaa-football-championships-hq/',
    note: 'Membership explicitly includes private/parochial schools. No single championships index exists — each sport has its own HQ page.', searched: true },

  { st: 'NV', state: 'Nevada', acronym: 'NIAA', name: 'Nevada Interscholastic Activities Association',
    site: 'https://niaa.com/', accent: '#6B2A4E',
    logoSource: 'https://niaa.com/apple-touch-icon.png',
    champs: 'https://niaa.com/navbar-fallsports',
    note: '⚠️ NIAA publishes no clean logo — 12 candidate paths all fail; only a 45×45 icon exists. Membership includes privates (Bishop Gorman, Faith Lutheran).', searched: true },

  { st: 'NH', state: 'New Hampshire', acronym: 'NHIAA', name: 'New Hampshire Interscholastic Athletic Association',
    site: 'https://www.nhiaa.org/', accent: '#2A6B7A',
    logoSource: 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/586/2026/04/21101458/NHIAA-Logo.png',
    champs: 'https://www.nhiaa.org/tournament/information/',
    note: 'NH has NO in-state private state-championship association — the independent lane is NEPSAC, which is regional.',
    others: [
      B('NEPSAC', 'New England Preparatory School Athletic Council', 'https://nepsac.org/', 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/328/2022/05/05100851/np.png', 'https://nepsac.org/tournaments/tournament-postseason/', 'independent', { multiState: true }),
    ] },

  { st: 'NJ', state: 'New Jersey', acronym: 'NJSIAA', name: 'New Jersey State Interscholastic Athletic Association',
    site: 'https://www.njsiaa.org/', accent: '#2E5E4E',
    logoSource: 'https://www.njsiaa.org/themes/njsiaa/images/logo.svg',
    champs: 'https://www.njsiaa.org/sports',
    note: 'NJ Catholic and parochial schools compete inside NJSIAA’s Non-Public groups.',
    others: [
      B('NJISAA', 'New Jersey Independent School Athletic Association', null, null, null, 'independent', { noSite: true,
        evidence: '⚠️ njisaa.org is a PARKED GoDaddy lander — do not link. Active: "NJISAA Prep A and B Championships 2026" are hosted publicly and member schools list NJISAA affiliation. Parent body NJAIS has no athletics section.' }),
    ] },

  { st: 'NM', state: 'New Mexico', acronym: 'NMAA', name: 'New Mexico Activities Association',
    site: 'https://www.nmact.org/', accent: '#B4562A',
    logoSource: 'https://www.nmact.org/wp-content/uploads/2017/06/nmaa-logo-full.png',
    champs: 'https://www.nmact.org/sports/', searched: true },

  { st: 'NY', state: 'New York', acronym: 'NYSPHSAA', name: 'New York State Public High School Athletic Association',
    site: 'https://nysphsaa.org/', accent: '#2A4E8B',
    logoSource: 'https://dbukjj6eu5tsf.cloudfront.net/nysphsaa.org/images/responsive_2021/main_logo-2.png',
    champs: 'https://nysphsaa.org/',
    note: '⚠️ New York City sits OUTSIDE NYSPHSAA. PSAL (public) and the NY CHSAA (Catholic) run their own championships.',
    others: [
      B('PSAL', 'Public Schools Athletic League (New York City)', 'https://www.psal.org/', 'https://www.psal.org/images/PSAL/bg-logo.png', 'https://www.psal.org/championships/', 'city'),
      B('CHSAA', 'NY State Catholic High School Athletic Association', 'https://www.chsaany.org/', 'https://cdn1.sportngin.com/attachments/touch_icon_graphic/ec02-163506321/CatholicHSAthlAssoc_Crest_size_120.png', 'https://www.chsaany.org/page/show/6300394-sports', 'private',
        { note: '⚠️ chsaa.ORG is COLORADO’s CHSAA. New York’s is chsaany.org.' }),
      B('NYSAISAA', 'NYSAIS Athletic Association', 'https://www.nysais.org/athletics/', 'https://www.nysais.org/wp-content/themes/Nysias-theme/images/logo.svg', 'https://www.nysais.org/athletics/', 'independent'),
      B('MMAA', 'Monsignor Martin HS Athletic Association (Buffalo)', 'https://www.monsignormartinathletics.com/', 'https://static.wixstatic.com/media/df175c_39a4cd78bf4b46e1ac22248f67cf5264~mv2.png', 'https://www.monsignormartinathletics.com/about-the-league', 'private'),
    ] },

  { st: 'NC', state: 'North Carolina', acronym: 'NCHSAA', name: 'North Carolina High School Athletic Association',
    site: 'https://www.nchsaa.org/', accent: '#2A5E8B',
    logoSource: 'https://www.nchsaa.org/wp-content/uploads/2023/08/Color-2nd-nchsaa-logo.png',
    champs: 'https://www.nchsaa.org/sports-championships/',
    others: [
      B('NCISAA', 'North Carolina Independent Schools Athletic Association', 'https://www.ncisaa.org/', 'https://www.ncisaa.org/wp-content/uploads/2020/06/weblogo.jpg', 'https://www.ncisaa.org/recognition/wells-fargo-cup/past-champions/', 'independent'),
      B('NCCSA', 'North Carolina Christian School Association', 'https://www.nccsa.org/programs/athletics/', 'https://www.nccsa.org/wp-content/themes/nccsa-2016/img/nccsa-logo-white.png', 'https://www.nccsa.org/programs/athletics/', 'christian'),
      B('CAASC', 'Carolina Athletic Association for Schools of Choice', 'https://www.thecaa4sc.com/', 'https://cdn4.sportngin.com/attachments/banner_graphic/7ba6-146482083/CAA4SC_logo.png', 'https://www.thecaa4sc.com/page/show/6001203-championship-central', 'charter'),
      B('NCHEAC', 'NC for Home Education Athletic Commission', 'https://www.ncheac.com/', 'https://cdn1.sportngin.com/attachments/logo_graphic/7f60-213901064/NCHEAC_Primary_Logo_White_Bkgd_800x800_small.jpg', 'https://www.ncheac.com/ncheacsports', 'homeschool'),
    ] },

  { st: 'ND', state: 'North Dakota', acronym: 'NDHSAA', name: 'North Dakota High School Activities Association',
    site: 'https://ndhsaa.com/', accent: '#3E5E2A',
    logoSource: 'https://ndhsaa.com/favicon.svg',
    champs: 'https://ndhsaanow.com/champions/football',
    note: 'Parochial schools (Shanley, Bishop Ryan) compete inside NDHSAA. No standalone logo file exists — the header mark is inline SVG.', searched: true },

  { st: 'OH', state: 'Ohio', acronym: 'OHSAA', name: 'Ohio High School Athletic Association',
    site: 'https://www.ohsaa.org/', accent: '#8B2E2E',
    logoSource: 'https://ohsaaweb.blob.core.windows.net/files/2021OHSAAPrimaryMarkNoBkgnd.png',
    champs: 'https://www.ohsaa.org/Sports',
    others: [
      B('OCSAA', 'Ohio Christian School Athletic Association', 'https://www.ohiocsaa.org/', 'https://www.ohiocsaa.org/ocsaaindextop.jpg', 'https://www.ohiocsaa.org/brackets.htm', 'christian'),
    ] },

  { st: 'OK', state: 'Oklahoma', acronym: 'OSSAA', name: 'Oklahoma Secondary School Activities Association',
    site: 'https://ossaaillustrated.com/', accent: '#7A3E2A',
    logoSource: 'https://ossaaillustrated.com/wp-content/uploads/2022/11/OSSAALogo-1.jpeg',
    champs: 'https://ossaaillustrated.com/state-champions',
    note: '⚠️ Corrected — ossaa.com now 301s to ossaaillustrated.com. There is NO "Oklahoma Christian School Athletic Association"; that acronym resolves to Ohio’s body.',
    others: [
      B('HCAA', 'Heartland Christian Athletic Association', 'https://ocsaa.net/', 'https://ocsaa.net/p/clubs/5161/1787585717/message.pic', 'https://ocsaa.net/clubs/5161/announcements', 'christian', { multiState: true }),
    ] },

  { st: 'OR', state: 'Oregon', acronym: 'OSAA', name: 'Oregon School Activities Association',
    site: 'https://www.osaa.org/', accent: '#2A6B5E',
    logoSource: 'https://www.osaa.org/images/logos/OSAACircle.png',
    champs: 'https://www.osaa.org/activities',
    note: 'Membership includes the private schools. Only a 32×32 mark is published.', searched: true },

  { st: 'PA', state: 'Pennsylvania', acronym: 'PIAA', name: 'Pennsylvania Interscholastic Athletic Association',
    site: 'https://www.piaa.org/', accent: '#2E4E6B',
    logoSource: 'https://www.piaa.org/assets/images/PIAA-logo.svg',
    champs: 'https://www.piaa.org/Championships/',
    note: 'PCIAA is defunct — PA Catholic schools have competed in PIAA since the 2000s.',
    others: [
      B('PAISAA', 'Pennsylvania Independent Schools Athletic Association', 'https://www.paisaasports.org/', null, 'https://www.paisaasports.org/page/show/925106-championships', 'independent'),
    ] },

  { st: 'RI', state: 'Rhode Island', acronym: 'RIIL', name: 'Rhode Island Interscholastic League',
    site: 'https://www.riil.org/', accent: '#5E2A3E',
    logoSource: 'https://www.riil.org/resources/_LogoSmall.png',
    champs: 'https://www.riil.org/SportPages/SportPageRanking.aspx',
    note: 'RIIL membership already includes RI private and parochial schools.',
    others: [
      B('NEPSAC', 'New England Preparatory School Athletic Council', 'https://nepsac.org/', 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/328/2022/05/05100851/np.png', 'https://nepsac.org/member-directory/', 'independent', { multiState: true }),
    ] },

  { st: 'SC', state: 'South Carolina', acronym: 'SCHSL', name: 'South Carolina High School League',
    site: 'https://schsl.org', accent: '#7A2A2A',
    logoSource: 'https://schsl.org/wp-content/uploads/2021/05/SCHSL-header-logo-mobile.jpg',
    champs: 'https://schsl.org/archives/category/championship-information',
    others: [
      B('SCISA', 'South Carolina Independent School Association', 'https://www.scisa.org', 'https://www.scisa.org/images/logo.png', 'https://www.scisa.org/athletics.html', 'independent'),
      B('SCACS', 'South Carolina Association of Christian Schools', 'https://christianeducation.org', 'https://christianeducation.org/wp-content/themes/theme1807/images/logo.png', 'https://christianeducation.org/athletics', 'christian'),
    ] },

  { st: 'SD', state: 'South Dakota', acronym: 'SDHSAA', name: 'South Dakota High School Activities Association',
    site: 'https://sdhsaa.com', accent: '#4E5E7A',
    logoSource: 'https://sdhsaa.com/wp-content/uploads/sdhsaa-logo@2x.png',
    champs: 'https://sdhsaa.com/activity/boys-basketball-championships/',
    note: 'Private/parochial schools (O’Gorman, Sioux Falls Christian, Roncalli) are inside SDHSAA.', searched: true },

  { st: 'TN', state: 'Tennessee', acronym: 'TSSAA', name: 'Tennessee Secondary School Athletic Association',
    site: 'https://tssaa.org/', accent: '#2A3E6B',
    logoSource: 'https://cms-files.tssaa.org/images/logos/tssaa-logo.png',
    champs: 'https://tssaasports.com/history/results/champions/byyear/',
    note: 'TSSAA Division II IS the private-school division — those are TSSAA state titles, not a separate body.',
    others: [
      B('TAACS', 'Tennessee Athletic Association of Christian Schools', 'https://www.tacs1.org/athletics/', 'https://www.tacs1.org/custom/images/logo.png', 'https://www.tacs1.org/athletics/', 'christian', { multiState: true }),
      B('TSIAA', 'Tennessee State Independent Athletic Association', 'https://www.tsiaa.com/', 'https://static.wixstatic.com/media/7d795e_af2fccf0e8a144c58d1b48881e594274~mv2.png', 'https://www.tsiaa.com/projects-8', 'independent', { dormant: true }),
    ] },

  { st: 'TX', state: 'Texas', acronym: 'UIL', name: 'University Interscholastic League',
    site: 'https://www.uiltexas.org/', accent: '#B4762A',
    logoSource: 'https://www.uiltexas.org/images/svg/uil-logo-web.svg',
    champs: 'https://www.uiltexas.org/athletics/sports',
    others: [
      B('TAPPS', 'Texas Association of Private and Parochial Schools', 'https://www.tapps.biz/', 'https://tapps.biz/wp-content/uploads/2025/07/image-13.png', 'https://tapps.biz/previous-results/', 'private'),
      B('TCAF', 'Texas Christian Athletic Fellowship', 'https://www.tcafellowship.com/', 'https://cdn4.sportngin.com/attachments/touch_icon_graphic/dd42-129504256/touch-icon_size_192.png', 'https://www.tcafellowship.com/athletics', 'christian'),
      B('TAIAO', 'Texas Association of Independent Athletic Organizations', 'https://taiao.org/', 'https://taiao.org/assets/taiao-logo-DiliWHwR.png', 'https://taiao.org/sports', 'independent'),
      B('TCSAAL', 'Texas Charter School Academic & Athletic League', 'https://texascharter.vensport.com/', 'https://s3.amazonaws.com/rsportz-production/file_assets/attachments/000/007/983/original/TCSAAL_logo_white_1.png', 'https://texascharter.vensport.com/pages/archives', 'charter'),
      B('T-CAL', 'Texas Christian Athletic League', 'https://www.teamtcal.org/', 'https://www.teamtcal.org/storage/2019/01/tcal_png-2.png', null, 'christian', { multiState: true }),
    ] },

  { st: 'UT', state: 'Utah', acronym: 'UHSAA', name: 'Utah High School Activities Association',
    site: 'https://uhsaa.org/', accent: '#8B4E3E',
    logoSource: 'https://uhsaa.org/v2/wp-content/uploads/2025/05/ico512-280x280.webp',
    champs: 'https://uhsaa.org/teams/',
    note: 'Private/Christian high schools (Layton Christian, Intermountain Christian, American Heritage) are inside UHSAA.', searched: true },

  { st: 'VT', state: 'Vermont', acronym: 'VPA', name: "Vermont Principals' Association",
    site: 'https://vpaonline.org/', accent: '#2A7A4E',
    logoSource: 'https://vpaonline.org/wp-content/uploads/2025/06/VPA-Logo.png',
    champs: 'https://vpaonline.org/athletics/tournaments/',
    note: 'Single-body state — the independent academies (St. Johnsbury, Burr and Burton, Lyndon) are VPA members.', searched: true },

  { st: 'VA', state: 'Virginia', acronym: 'VHSL', name: 'Virginia High School League',
    site: 'https://www.vhsl.org/', accent: '#3E2A6B',
    logoSource: 'https://www.vhsl.org/wp-content/uploads/2017/04/VHSL-header-logo.png',
    champs: 'https://www.vhsl.org/athletics/',
    others: [
      B('VISAA', 'Virginia Independent Schools Athletic Association', 'https://www.visaa.org/', 'https://www.visaa.org/img/layout/logo.png', 'https://www.visaa.org/sports', 'independent',
        { note: 'Where VA Catholic schools crown — there is no separate VA Catholic body.' }),
      B('VHAC', 'Virginia Homeschool Athletic Conference', 'https://www.vhaconference.com/', 'https://static.wixstatic.com/media/562482_5ef5ced981884bbb90ce7f1f2a7d2a0f~mv2.png', 'https://www.vhaconference.com/basketball', 'homeschool'),
      B('VACA', 'Virginia Association of Christian Athletics', 'https://79230.digitalsports.com/', null, null, 'christian',
        { note: '⚠️ Real but weakly sourced — titles documented only on member-school pages. Needs a phone call, not an automated ingest.' }),
    ] },

  { st: 'WA', state: 'Washington', acronym: 'WIAA', name: 'Washington Interscholastic Activities Association',
    site: 'https://www.wiaa.com/', accent: '#2A5E6B',
    logoSource: 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/652/2023/03/19194847/logow.png',
    champs: 'https://www.wiaa.com/state/',
    note: '⚠️ A different organisation from Wisconsin’s WIAA — same acronym, different domain. Privates are WIAA members.', searched: true },

  { st: 'WV', state: 'West Virginia', acronym: 'WVSSAC', name: 'West Virginia Secondary School Activities Commission',
    site: 'https://www.wvssac.org/', accent: '#6B4E2A',
    logoSource: 'https://assets-rst7.rschooltoday.com/rst7files/uploads/sites/588/2026/04/25132154/header_logo.png',
    champs: 'https://www.wvssac.org/sports/',
    others: [
      B('WVCEA', 'West Virginia Christian Education Association', 'https://wvcea.org/athletic-program/', 'https://wvcea.org/wp-content/uploads/2026/07/24398_WVCEA_SG-01.png', 'https://wvcea.org/athletic-program/', 'christian'),
    ] },

  { st: 'WI', state: 'Wisconsin', acronym: 'WIAA', name: 'Wisconsin Interscholastic Athletic Association',
    site: 'https://www.wiaawi.org/', accent: '#8B2E4E',
    logoSource: 'https://www.wiaawi.org/sites/default/files/2026-04/logo.svg',
    champs: 'https://www.wiaawi.org/sports',
    note: '⚠️ A different organisation from Washington’s WIAA. WISAA merged into WIAA in 2000 — do not list it as live.',
    others: [
      B('WACS', 'Wisconsin Association of Christian Schools', 'https://www.wacschools.org/athletics', 'https://cdn.prod.website-files.com/64dc2531ea6035b003f14db1/64dc2531ea6035b003f14e53_wacsathletics_web_color.png', 'https://www.wacschools.org/athletics', 'christian'),
    ] },

  { st: 'WY', state: 'Wyoming', acronym: 'WHSAA', name: 'Wyoming High School Activities Association',
    site: 'https://www.whsaa.org/', accent: '#7A5E3E',
    logoSource: 'https://www.whsaa.org/images/WHSAA.png',
    champs: 'https://www.whsaa.org/archives/sportsarchives.html',
    note: 'WHSAA admits accredited private high schools, and state law lets homeschool students play for their local public school — which removes the usual reason a separate body forms.', searched: true },
].map((b) => ({
  logo: null,              // set only by a HubSpot-hosted pull; see markFor()
  logoSource: null,
  champs: null,
  others: [],
  note: '',
  searched: false,         // true = independent bodies were looked for
  siteVerified: true,      // every site above was fetched and loaded 2026-09-01
  ...b,
}))

export const bodyCount = STATE_BODIES.length

/** Every entity — the public body plus each independent/private/city association. */
export function allEntities(list = STATE_BODIES) {
  const out = []
  for (const b of list) {
    out.push({ ...b, primary: true, kind: 'public', key: `${b.st}-${b.acronym}` })
    for (const o of (b.others || [])) {
      out.push({ st: b.st, state: b.state, accent: b.accent, siteVerified: true, ...o, primary: false, key: `${b.st}-${o.acronym}` })
    }
  }
  return out
}

/** Match our own records to a body by state name. */
export function forState(stateName, list = STATE_BODIES) {
  const s = String(stateName || '').trim().toLowerCase()
  return list.find((b) => b.state.toLowerCase() === s) || null
}

/**
 * ⚠️ THE MARK IS SR-SET TYPE UNTIL AN OWNED FILE EXISTS.
 * `kind: 'logo'` only ever happens for an SR-hosted path — an http(s) URL to
 * somebody else's server is REFUSED here rather than rendered, so the
 * owned-assets rule cannot be broken by editing the registry. Permission to use
 * a mark is not the same as hosting it: a link to their server still dies.
 */
export function markFor(entity) {
  const logo = String(entity?.logo || '')
  if (logo && !/^https?:/i.test(logo)) return { kind: 'logo', src: logo }
  return { kind: 'type', text: entity?.acronym || entity?.st || '?', accent: entity?.accent || 'var(--brass)' }
}
